import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, CalendarDays, ChevronDown, Clock, FileText, Plus, X } from 'lucide-react';
import Modal from './Modal';
import { Checkbox, InteractiveInput, MultiFile } from './FieldComponents';

const DEFAULT_REMINDERS = [30, 60, 90];
const PRESET_REMINDERS = [
    { label: '1 / 3 / 7', values: [1, 3, 7] },
    { label: '7 / 14 / 30', values: [7, 14, 30] },
];

const dateKey = (date) => date.toLocaleDateString('en-CA');

const normalizeDateTime = (value) => {
    if (!value || typeof value !== 'string') return null;
    return value.includes('T') ? value : value.replace(' ', 'T');
};

const entryDate = (entry) => {
    const start = normalizeDateTime(entry?.start_date);
    return start ? start.split('T')[0] : entry?.deadline_date || null;
};

const displayDate = (value) => {
    if (!value) return 'Date needed';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const count = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let index = 0; index < firstDay.getDay(); index += 1) {
        days.push({ blank: true, key: `blank-${index}` });
    }

    for (let day = 1; day <= count; day += 1) {
        const current = new Date(year, month, day);
        days.push({
            key: dateKey(current),
            date: dateKey(current),
            number: day,
        });
    }

    return days;
};

const reminderDate = (deadlineDate, daysBefore) => {
    if (!deadlineDate || daysBefore === '' || daysBefore === null || daysBefore === undefined) return null;
    const value = Number(daysBefore);
    if (!Number.isFinite(value)) return null;
    const date = new Date(`${deadlineDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    date.setDate(date.getDate() - value);
    return date;
};

const compactEntryLabel = (entry) => {
    const isDraft = entry?.is_draft || entry?.source === 'case_calendar_draft';
    return `${entry?.calendar_title || 'Calendar entry'}${isDraft ? ' (Draft)' : ''}`;
};

const parseUserInput = (value) => (
    String(value || '')
        .split(',')
        .map((user) => user.trim())
        .filter(Boolean)
);

export const CalendarEntryModal = ({
    deadline,
    setDeadline,
    editingDeadline,
    submitting,
    caseTeamEmails,
    calendarEntries,
    onSave,
    onDelete,
    onClose,
}) => {
    const selectedDate = deadline.date || dateKey(new Date());
    const [pickerMonth, setPickerMonth] = useState(() => {
        const parsed = new Date(`${selectedDate}T00:00:00`);
        return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    });

    useEffect(() => {
        if (!deadline.date) return;
        const parsed = new Date(`${deadline.date}T00:00:00`);
        if (!Number.isNaN(parsed.getTime())) {
            setPickerMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
        }
    }, [deadline.date]);

    const isDraft = Boolean(deadline.isDraft);
    const calendarStatus = deadline.calendarStatus || (isDraft ? 'draft' : 'pushed');
    const modalTitle = editingDeadline ? (isDraft ? 'Edit Calendar Draft' : 'Edit Calendar Entry') : 'Create Calendar Entry';
    const users = Array.isArray(deadline.users) ? deadline.users.filter(Boolean) : [];
    const [userInputValue, setUserInputValue] = useState(() => users.join(', '));
    const reminders = Array.isArray(deadline.reminders) ? deadline.reminders : [];
    const selectedEntries = useMemo(
        () => (calendarEntries || []).filter((entry) => entryDate(entry) === selectedDate),
        [calendarEntries, selectedDate]
    );
    const entryCountByDate = useMemo(() => {
        const counts = {};
        (calendarEntries || []).forEach((entry) => {
            const key = entryDate(entry);
            if (!key) return;
            const isEntryDraft = entry?.is_draft || entry?.source === 'case_calendar_draft';
            counts[key] = counts[key] || { live: 0, draft: 0 };
            counts[key][isEntryDraft ? 'draft' : 'live'] += 1;
        });
        return counts;
    }, [calendarEntries]);

    const caseTeamOptions = ['picalendar@dalyblack.com', 'firstparty@dalyblack.com', ...Object.entries(caseTeamEmails || {}).map(([role, email]) => `${email} (First ${role})`)];
    const currentReminderValues = reminders.length > 0 ? reminders : (deadline.defaultReminders ? DEFAULT_REMINDERS : []);
    const dateObject = deadline.date ? new Date(`${deadline.date}T00:00:00`) : null;
    const isWeekend = dateObject && !Number.isNaN(dateObject.getTime()) && [0, 6].includes(dateObject.getDay());

    useEffect(() => {
        setUserInputValue(users.join(', '));
    }, [deadline.event_uuid, deadline.draft_id]);

    const updateField = (key, value) => setDeadline((prev) => ({ ...prev, [key]: value }));
    const selectDate = (date) => {
        setDeadline((prev) => ({
            ...prev,
            date,
            endDate: prev.endDate || date,
        }));
    };
    const setReminderPreset = (values) => {
        setDeadline((prev) => ({
            ...prev,
            defaultReminders: false,
            reminders: values,
        }));
    };
    const setCustomReminder = (index, value) => {
        const next = [...reminders];
        next[index] = value === '' ? '' : Number(value);
        setDeadline((prev) => ({ ...prev, defaultReminders: false, reminders: next }));
    };
    const removeReminder = (index) => {
        setDeadline((prev) => ({ ...prev, reminders: reminders.filter((_, reminderIndex) => reminderIndex !== index) }));
    };
    const addReminder = () => {
        setDeadline((prev) => ({ ...prev, defaultReminders: false, reminders: [...reminders, 0] }));
    };

    return (
        <Modal
            wider
            title={modalTitle}
            onClose={onClose}
            header={
                <div className='modal-header-actions calendar-entry-modal-actions'>
                    <button className='action' onClick={onSave} disabled={submitting}>{editingDeadline ? 'Update' : 'Create'}</button>
                    {editingDeadline && <button className='action destroy' disabled={submitting} onClick={onDelete}>{isDraft ? 'Delete Draft' : 'Delete'}</button>}
                    <button className='action alt' onClick={onClose} disabled={submitting}>Cancel</button>
                </div>
            }
        >
            <div className='modal-content-wrapper calendar-entry-modal'>
                <aside className='calendar-entry-picker-panel'>
                    <div className='calendar-entry-panel-header'>
                        <button className='calendar-entry-icon-button' onClick={() => setPickerMonth(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() - 1, 1))} title='Previous month'>
                            <ArrowLeft size={16} />
                        </button>
                        <div className='calendar-entry-month-title'>{pickerMonth.toLocaleString('default', { month: 'long' })} {pickerMonth.getFullYear()}</div>
                        <button className='calendar-entry-icon-button' onClick={() => setPickerMonth(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() + 1, 1))} title='Next month'>
                            <ArrowRight size={16} />
                        </button>
                    </div>
                    <div className='calendar-entry-weekdays'>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
                    </div>
                    <div className='calendar-entry-date-grid'>
                        {daysInMonth(pickerMonth).map((day) => {
                            if (day.blank) return <div key={day.key} className='calendar-entry-date-cell blank' />;
                            const counts = entryCountByDate[day.date] || { live: 0, draft: 0 };
                            return (
                                <button
                                    key={day.date}
                                    className={`calendar-entry-date-cell ${day.date === selectedDate ? 'selected' : ''} ${counts.live || counts.draft ? 'has-items' : ''}`}
                                    onClick={() => selectDate(day.date)}
                                >
                                    <span>{day.number}</span>
                                    {(counts.live > 0 || counts.draft > 0) && (
                                        <span className='calendar-entry-date-dots'>
                                            {counts.live > 0 && <i className='live' />}
                                            {counts.draft > 0 && <i className='draft' />}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div className='calendar-entry-selected-date'>
                        <div className='calendar-entry-small-title'>{displayDate(selectedDate)}</div>
                        {selectedEntries.length > 0 ? selectedEntries.map((entry, index) => (
                            <button key={`${entry.event_uuid || entry.draft_id || index}`} className='calendar-entry-related-item' onClick={() => selectDate(entryDate(entry))}>
                                {compactEntryLabel(entry)}
                            </button>
                        )) : <span className='subtext'>No entries on this date.</span>}
                    </div>
                </aside>

                <main className='calendar-entry-form-panel'>
                    <section className='calendar-entry-summary-card'>
                        <div className='calendar-entry-summary-topline'>
                            <div className='filter-group calendar-entry-status-select'>
                                <div className='select-wrapper'>
                                    <select
                                        className='filter-select'
                                        value={calendarStatus}
                                        onChange={(event) => updateField('calendarStatus', event.target.value)}
                                    >
                                        <option value='draft'>Draft</option>
                                        <option value='pushed'>Pushed</option>
                                    </select>
                                    <ChevronDown size={16} className='select-chevron' />
                                </div>
                            </div>
                            <span>{calendarStatus === 'draft' ? 'CaseDB draft only' : editingDeadline ? 'Synced to recipient calendars' : 'Will create recipient calendar events'}</span>
                        </div>
                        <h3>{deadline.subject || 'Untitled calendar entry'}</h3>
                        <div className='calendar-entry-summary-grid'>
                            <span><CalendarDays size={15} />{displayDate(deadline.date)}</span>
                            <span><Clock size={15} />{deadline.isAllDay ? 'All day' : `${deadline.time || 'Start time'}${deadline.endTime ? ` - ${deadline.endTime}` : ''}`}</span>
                            <span><FileText size={15} />{users.length > 0 ? `${users.length} recipient${users.length === 1 ? '' : 's'}` : 'No recipients'}</span>
                        </div>
                    </section>

                    <section className='calendar-entry-section'>
                        <div className='calendar-entry-section-title'>Basics</div>
                        <div className='calendar-entry-form-grid'>
                            <div className='form-group full'>
                                <label className='subtext'>Title</label>
                                <input type='text' value={deadline.subject} onChange={(event) => updateField('subject', event.target.value)} placeholder='Enter calendar title' />
                            </div>
                            <Checkbox label='All day' value={deadline.isAllDay} onChange={() => setDeadline((prev) => ({ ...prev, isAllDay: !prev.isAllDay }))} />
                        </div>
                    </section>

                    <section className='calendar-entry-section'>
                        <div className='calendar-entry-section-title'>Date and Time</div>
                        <div className='calendar-entry-form-grid two-column'>
                            <div className='form-group'>
                                <label className='subtext'>Start Date</label>
                                <input type='date' value={deadline.date} onChange={(event) => selectDate(event.target.value)} />
                            </div>
                            {!deadline.isAllDay && (
                                <div className='form-group'>
                                    <label className='subtext'>Start Time</label>
                                    <input type='time' value={deadline.time} onChange={(event) => updateField('time', event.target.value)} />
                                </div>
                            )}
                            {!deadline.isAllDay && (
                                <>
                                    <div className='form-group'>
                                        <label className='subtext'>End Date</label>
                                        <input type='date' value={deadline.endDate} onChange={(event) => updateField('endDate', event.target.value)} />
                                    </div>
                                    <div className='form-group'>
                                        <label className='subtext'>End Time</label>
                                        <input type='time' value={deadline.endTime} onChange={(event) => updateField('endTime', event.target.value)} />
                                    </div>
                                </>
                            )}
                        </div>
                        {isWeekend && <div className='calendar-entry-warning'>This date falls on a {dateObject.toLocaleString('default', { weekday: 'long' })}. Consider moving it to a weekday.</div>}
                    </section>

                    <section className='calendar-entry-section'>
                        <div className='calendar-entry-section-title'>Calendars and Recipients</div>
                        <InteractiveInput
                            value={userInputValue}
                            onChange={(value) => {
                                setUserInputValue(value);
                                updateField('users', parseUserInput(value));
                            }}
                            options={caseTeamOptions}
                            placeholder='Enter user emails, separated by commas'
                        />
                    </section>

                    <section className='calendar-entry-section'>
                        <div className='calendar-entry-section-title'>Reminders</div>
                        <div className='calendar-entry-reminders'>
                            {currentReminderValues.map((reminder, index) => {
                                const computedDate = reminderDate(deadline.date, reminder);
                                const computedWeekend = computedDate && [0, 6].includes(computedDate.getDay());
                                return (
                                    <div key={`${reminder}-${index}`} className='calendar-entry-reminder-row'>
                                        <input type='number' className='reminder-input spec' value={reminder === 0 ? '' : reminder} onChange={(event) => setCustomReminder(index, event.target.value)} />
                                        <span>days before</span>
                                        <span className='small-text' style={{ lineHeight: '24px' }}>{computedDate ? computedDate.toLocaleDateString() : 'Date needed'}</span>
                                        {computedWeekend && <span className='calendar-entry-weekend-pill'>{computedDate.toLocaleString('default', { weekday: 'short' })}</span>}
                                        <button onClick={() => removeReminder(index)} title='Remove reminder'><X size={15} /></button>
                                    </div>
                                );
                            })}
                            <button className='calendar-entry-add-reminder' onClick={addReminder}><Plus size={16} /> Add reminder</button>
                            <div className='calendar-entry-reminder-presets'>
                                <button className={`calendar-entry-preset ${deadline.defaultReminders ? 'active' : ''}`} onClick={() => setDeadline((prev) => ({ ...prev, defaultReminders: true, reminders: DEFAULT_REMINDERS }))}>Default 30 / 60 / 90</button>
                                {PRESET_REMINDERS.map((preset) => <button key={preset.label} className='calendar-entry-preset' onClick={() => setReminderPreset(preset.values)}>{preset.label}</button>)}
                            </div>
                        </div>
                    </section>

                    <section className='calendar-entry-section'>
                        <div className='calendar-entry-section-title'>Notes and Source</div>
                        <textarea value={deadline.notes || ''} rows={4} onChange={(event) => updateField('notes', event.target.value)} placeholder='Enter notes or source context' />
                    </section>

                    <section className='calendar-entry-section'>
                        <div className='calendar-entry-section-title'>Attachments</div>
                        <MultiFile value={deadline.files || []} onChange={(files) => updateField('files', files)} disable={editingDeadline} />
                    </section>
                </main>
            </div>
        </Modal>
    );
};